import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import { useAuth } from 'react-oidc-context'
import { needsRenewal, renewSession } from '@/lib/auth/renew-session'
import { isDeadSessionError } from '../is-dead-session-error'

/**
 * The login iframe's parent window owns the refresh token. Renewing in both
 * races the one-time-use token and IDS6 returns 400 invalid_grant to the loser,
 * so every trigger below is confined to the top window.
 */
const IS_TOP_WINDOW = typeof window === 'undefined' || window.parent === window

/**
 * Owns access-token renewal for this app.
 *
 * The manager runs with `automaticSilentRenew: false`, so the library no longer
 * renews behind our back — this component is the single subscriber, and every
 * trigger (approaching expiry, a token already expired at startup, tab refocus,
 * and 401 recovery in useClientApi) funnels through the one single-flight
 * `renewSession`. That is the point: two uncoordinated renewals rotate the same
 * one-time-use refresh token and the loser's failure kills the session.
 *
 * Renders nothing. Mounted only when this app owns its own OIDC session; when
 * embedded with host-provided config the host is authoritative.
 */
const SessionRenewalBridge = () => {
  const { events, isLoading, removeUser, user } = useAuth()

  const renew = useCallback(() => {
    void renewSession().catch((error: unknown) => {
      // Only invalid_grant proves the refresh token is dead. A network blip,
      // timeout or 5xx says nothing about the session, and evicting the user
      // over one is what turned every hiccup into a logout.
      if (isDeadSessionError(error)) void removeUser()
    })
  }, [removeUser])

  // Proactive: raised SCHEDULED_RENEW_LEAD_SECONDS before the token expires.
  useEffect(() => {
    if (!IS_TOP_WINDOW) return
    return events.addAccessTokenExpiring(renew)
  }, [events, renew])

  // The expiring event covers an approaching expiry, but the manager cancels
  // that timer outright once the token is already past expiry — so a token that
  // lapsed while the tab was closed gets no scheduled renewal at all.
  const attemptedLoadRenewRef = useRef(false)
  useLayoutEffect(() => {
    if (!IS_TOP_WINDOW) return
    if (isLoading || attemptedLoadRenewRef.current) return
    // Anonymous visitors have no stored user and must not contact the IdP.
    // `user.expired` is a live getter; `isAuthenticated` is a snapshot taken
    // when the user was loaded and goes stale as the token ages.
    if (!user?.expired) return

    // Never reset: startup recovery runs exactly once per app load, or a
    // permanently dead refresh token would retry forever.
    attemptedLoadRenewRef.current = true
    renew()
  }, [isLoading, user, renew])

  // Browsers throttle timers in backgrounded tabs, so the scheduled renewal can
  // fire late or not at all while the user is away. At a 10-minute token that
  // means returning from a coffee break to a dead token and a 401 on the first
  // refetch. Re-check whenever the tab comes back to the front.
  useEffect(() => {
    if (!IS_TOP_WINDOW) return

    const renewIfStale = () => {
      if (document.visibilityState !== 'visible') return
      if (!user || !needsRenewal(user.expires_at)) return
      renew()
    }

    document.addEventListener('visibilitychange', renewIfStale)
    window.addEventListener('focus', renewIfStale)
    return () => {
      document.removeEventListener('visibilitychange', renewIfStale)
      window.removeEventListener('focus', renewIfStale)
    }
  }, [user, renew])

  return null
}

export default SessionRenewalBridge
